import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import './ModelViewer.css';
import { getBrainInfo, SPLIT_DISTANCE } from './brainData';
import { useFocusMeshOnLoad } from './useFocusMeshOnLoad';
import { useMeshSelection } from './useMeshSelection';
import ModelSearchBar from './ModelSearchBar';
import { HIGHLIGHT_COLOR } from './modelViewerUtils';

const ANIMATION_DURATION = 500;

function BrainFull() {
  const navigate = useNavigate();
  const modelRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isSplit, setIsSplit] = useState(false);

  const originalMaterialsRef = useRef(new Map());
  const highlightedMeshRef = useRef(null);
  
  const brainPartsRef = useRef({ left: [], right: [] });
  const animationRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const raycasterRef = useRef(new THREE.Raycaster());

  const forceRender = useCallback(() => {
    const mv = modelRef.current;
    if (!mv) return;

    if (mv.requestUpdate) mv.requestUpdate();

    try {
      const symbols = [
        ...Object.getOwnPropertySymbols(mv),
        ...(mv.__proto__ ? Object.getOwnPropertySymbols(mv.__proto__) : [])
      ];
      const needsRenderSymbol = symbols.find(s => s?.description === 'needsRender');
      if (needsRenderSymbol && typeof mv[needsRenderSymbol] === 'function') {
        mv[needsRenderSymbol]();
      }
    } catch (e) { }

    try {
      const expAttr = mv.getAttribute('exposure') || '1';
      const exp = parseFloat(expAttr);
      mv.setAttribute('exposure', (exp + 0.000001).toString());
      requestAnimationFrame(() => {
        if (modelRef.current) modelRef.current.setAttribute('exposure', expAttr);
      });
    } catch (e) { }
  }, []);

  const getInternalScene = useCallback(() => {
    if (sceneRef.current && cameraRef.current) return { scene: sceneRef.current, camera: cameraRef.current };
    
    const mv = modelRef.current;
    if (!mv) return null;

    let scene = null;
    let camera = null;

    const symbols = Object.getOwnPropertySymbols(mv);
    for (const sym of symbols) {
      const val = mv[sym];
      if (val && val.isScene) scene = val;
      if (val && (val.isCamera || val.isPerspectiveCamera)) camera = val;
      if (val && val.scene && val.scene.isScene) scene = val.scene;
      if (val && val.camera && (val.camera.isCamera || val.camera.isPerspectiveCamera)) camera = val.camera;
    }

    if (!scene) {
      const keys = Object.getOwnPropertyNames(mv);
      for (const key of keys) {
        try {
          const val = mv[key];
          if (val && val.isScene) scene = val;
          if (val && (val.isCamera || val.isPerspectiveCamera)) camera = val;
        } catch (e) {}
      }
    }

    if (scene) {
      sceneRef.current = scene;
      cameraRef.current = camera || mv.camera;
      return { scene: sceneRef.current, camera: cameraRef.current };
    }
    
    return null;
  }, []);

  const findClickedMesh = useCallback((clientX, clientY) => {
    const mv = modelRef.current;
    if (!mv) return null;

    const rect = mv.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const hit = mv.positionAndNormalFromPoint(x, y);
    if (!hit) return null;

    const mvMaterial = mv.materialFromPoint(x, y);
    const ctx = getInternalScene();
    
    if (!ctx || !ctx.camera) return { mesh: null, hit, meshName: mvMaterial?.name || 'Unknown', mvMaterial };

    const { scene, camera } = ctx;
    const ndcX = (x / rect.width) * 2 - 1;
    const ndcY = -(y / rect.height) * 2 + 1;

    raycasterRef.current.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
    const intersects = raycasterRef.current.intersectObjects(scene.children, true);

    let meshName = mvMaterial?.name || 'Unknown';
    let meshObj = null;

    if (intersects.length > 0) {
      const meshIntersect = intersects.find(i => {
        const obj = i.object;
        if (!obj.isMesh) return false;
        const name = (obj.name || '').toLowerCase();
        const matName = (obj.material?.name || '').toLowerCase();
        if (name.includes('shader') || name.includes('shadow') || name.includes('ground') || name.includes('helper')) return false;
        if (matName.includes('shader') || matName.includes('shadow') || matName.includes('ground')) return false;
        return true;
      });
      if (meshIntersect) {
        meshObj = meshIntersect.object;
        meshName = meshObj.name || meshObj.material?.name || meshName;
      }
    }

    return { mesh: meshObj, hit, meshName, mvMaterial };
  }, [getInternalScene]);

  const highlightMesh = useCallback((mesh) => {
    if (!mesh || !mesh.material) return;

    if (!originalMaterialsRef.current.has(mesh.uuid)) {
      const orig = mesh.material;
      let cloned;
      if (Array.isArray(orig)) {
        cloned = orig.map(m => (m && typeof m.clone === 'function') ? m.clone() : null).filter(Boolean);
      } else if (orig && typeof orig.clone === 'function') {
        cloned = orig.clone();
      }

      if (!cloned) return;
      mesh.material = cloned;

      const mats = Array.isArray(cloned) ? cloned : [cloned];
      const props = mats.map(m => ({
        color: (m.color && typeof m.color.clone === 'function') ? m.color.clone() : new THREE.Color(0xffffff),
        opacity: m.opacity ?? 1,
        transparent: !!m.transparent,
      }));
      originalMaterialsRef.current.set(mesh.uuid, props);
    }

    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach(m => {
      if (m && m.color && typeof m.color.set === 'function') {
        m.color.set(HIGHLIGHT_COLOR);
        m.opacity = 0.85;
        m.transparent = true;
      }
    });

    highlightedMeshRef.current = mesh;
    forceRender();
  }, [forceRender]);

  const resetHighlight = useCallback(() => {
    const high = highlightedMeshRef.current;
    if (!high || !high.isMesh) return;

    const props = originalMaterialsRef.current.get(high.uuid);
    if (!props) return;

    const mats = Array.isArray(high.material) ? high.material : [high.material];
    mats.forEach((m, i) => {
      const p = props[i];
      if (m && m.color && p && p.color) {
        m.color.set(p.color);
        m.opacity = p.opacity;
        m.transparent = p.transparent;
      }
    });

    highlightedMeshRef.current = null;
    forceRender();
  }, [forceRender]);

  const { selectedPart, hotspotPosition, screenSide, hotspotKey, applySelection, closeLabel } =
    useMeshSelection({ infoLookup: getBrainInfo, highlightMesh, resetHighlight });

  const animateSplit = useCallback((startTime, startPosLeft, endPosLeft, startPosRight, endPosRight) => {
    const now = performance.now();
    const progress = Math.min((now - startTime) / ANIMATION_DURATION, 1);
    const easeProgress = 1 - Math.pow(1 - progress, 3);

    const currentLeft = startPosLeft + (endPosLeft - startPosLeft) * easeProgress;
    const currentRight = startPosRight + (endPosRight - startPosRight) * easeProgress;

    const { left, right } = brainPartsRef.current;
    left.forEach(obj => { if (obj) obj.position.x = currentLeft; });
    right.forEach(obj => { if (obj) obj.position.x = currentRight; });

    forceRender();

    if (progress < 1) {
      animationRef.current = requestAnimationFrame(() =>
        animateSplit(startTime, startPosLeft, endPosLeft, startPosRight, endPosRight)
      );
    }
  }, [forceRender]);

  const toggleSplit = useCallback(() => {
    const newState = !isSplit;
    setIsSplit(newState);

    const { left, right } = brainPartsRef.current;
    if (left.length === 0 && right.length === 0) {
        const ctx = getInternalScene();
        if (ctx && ctx.scene) {
            ctx.scene.traverse((obj) => {
                if (obj.isMesh) {
                    if (obj.name.endsWith('_L')) left.push(obj);
                    else if (obj.name.endsWith('_R')) right.push(obj);
                }
            });
        }
    }

    if (animationRef.current) cancelAnimationFrame(animationRef.current);

    const sL = left[0]?.position.x || 0;
    const eL = newState ? SPLIT_DISTANCE : 0;
    const sR = right[0]?.position.x || 0;
    const eR = newState ? -SPLIT_DISTANCE : 0;

    animateSplit(performance.now(), sL, eL, sR, eR);
    closeLabel();
  }, [isSplit, getInternalScene, animateSplit, closeLabel]);

  const onReveal = useCallback(async (_mesh, entry) => {
    if (entry && entry.reveal === 'split' && !isSplit) {
      toggleSplit();
      await new Promise((res) => setTimeout(res, ANIMATION_DURATION + 80));
    }
  }, [isSplit, toggleSplit]);

  const onFound = useCallback((mesh, center, normal) => {
    applySelection(mesh, center, normal);
  }, [applySelection]);

  const { focusMeshIfPending, focusMesh } = useFocusMeshOnLoad({ modelRef, onFound, onReveal });

  useEffect(() => {
    const mv = modelRef.current;
    if (!mv) return;

    const handleLoad = () => {
      setIsLoading(false);
      const ctx = getInternalScene();
      if (ctx && ctx.scene) {
          const left = [], right = [];
          ctx.scene.traverse((obj) => {
              if (obj.isMesh) {
                  if (obj.name.endsWith('_L')) left.push(obj);
                  else if (obj.name.endsWith('_R')) right.push(obj);
              }
          });
          brainPartsRef.current = { left, right };
      }
      focusMeshIfPending();
    };

    mv.addEventListener('load', handleLoad);
    if (mv.loaded) handleLoad();
    return () => mv.removeEventListener('load', handleLoad);
  }, [getInternalScene, focusMeshIfPending]);

  const handleModelClick = useCallback((event) => {
    const meshData = findClickedMesh(event.clientX, event.clientY);

    if (meshData && meshData.hit && meshData.meshName && meshData.meshName !== 'Unknown') {
      applySelection(meshData.mesh, meshData.hit.position, meshData.hit.normal);
    } else {
      closeLabel();
    }
  }, [findClickedMesh, applySelection, closeLabel]);

  return (
    <div className="model-viewer-page skeleton-page skeleton-surface-click">
      <button onClick={() => navigate('/menu')} className="back-btn">← Back to Menu</button>

      <ModelSearchBar currentSystemId="brain" onFocusMesh={focusMesh} />

      {isLoading && (
        <div className="skeleton-mv-loading">
          <div className="skeleton-loading-spinner"></div>
          <p>Loading 3D Brain...</p>
        </div>
      )}

      <div className="skeleton-hint">Tap on brain parts to identify them</div>

      <model-viewer
        ref={modelRef}
        src="/models/Brain.glb"
        alt="3D Human Brain"
        ar
        ar-modes="webxr scene-viewer quick-look"
        camera-controls
        touch-action="none"
        interaction-prompt="none"
        loading="eager"
        reveal="auto"
        shadow-intensity="1"
        camera-orbit="0deg 75deg 0.5m"
        field-of-view="45deg"
        min-camera-orbit="auto auto 0.2m"
        max-camera-orbit="auto auto 4m"
        onClick={handleModelClick}
        style={{ width: '100%', height: '100%', backgroundColor: '#ffffff', cursor: 'var(--cursor-pointer)', '--progress-bar-color': '#00E5FF', '--progress-bar-height': '4px' }}
      >
        {selectedPart && hotspotPosition && (
          <div
            key={hotspotKey}
            className={`skeleton-leader-hotspot ${screenSide}`}
            slot="hotspot-part"
            data-position={hotspotPosition.position}
            data-normal={hotspotPosition.normal}
          >
            <div className="skeleton-leader-dot"></div>
            <div className="skeleton-leader-line"></div>
            <div className="skeleton-leader-label">
              <div className="skeleton-leader-content">
                <span className="skeleton-leader-english">{selectedPart.english}</span>
                <span className="skeleton-leader-latin">{selectedPart.latin}</span>
              </div>
              <button className="skeleton-leader-close" onClick={closeLabel}>✕</button>
            </div>
          </div>
        )}
      </model-viewer>

      <button
        className="brain-split-control-btn"
        onClick={toggleSplit}
      >
        <img 
          src={isSplit ? "https://cdn-icons-png.flaticon.com/512/16371/16371005.png" : "https://cdn-icons-png.flaticon.com/512/10137/10137060.png"} 
          alt="split-icon"
        />
      </button>
    </div>
  );
}

function Brain() {
  
  return <BrainFull />;
}

export default Brain;
